import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { getIcon } from '../data/iconMap';
import { getCategory, categoryUrl } from '../data/categories';
import { Button } from '../components/ui/button';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Top-level category landing page.
 * Shows a cinematic hero + grid of subcategories with images and icons.
 */
export default function CategoryHub() {
  const { categorySlug } = useParams();
  const category = getCategory(categorySlug);

  usePageTitle(category ? category.title : 'Category');

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-4xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Category not found
          </h1>
          <Link to="/" className="text-gold uppercase tracking-[0.2em] text-sm">
            ← Back home
          </Link>
        </div>
      </div>
    );
  }

  const HeroIcon = getIcon(category.icon);

  return (
    <div className="min-h-screen" data-testid={`category-hub-${category.slug}`}>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url('${category.hero}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/70 to-[#050C16]/30" />
        <div className="absolute inset-0 noise-texture opacity-40" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pb-16 w-full">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 mb-6">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-gold">{category.title}</span>
          </div>
          <div className="flex items-start gap-5 mb-4">
            <span className="w-12 h-12 border border-gold/50 flex items-center justify-center text-gold">
              <HeroIcon size={22} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">{category.tagline}</p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="category-title"
              >
                {category.title}
              </h1>
            </div>
          </div>
          <p className="max-w-2xl text-white/70 text-base leading-relaxed">{category.description}</p>
        </div>
      </section>

      {/* Subcategory grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <h2
            className="text-3xl sm:text-4xl font-medium tracking-tight text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Browse by <span className="text-gold">category</span>
          </h2>
          <Link to="/list/new">
            <Button
              variant="ghost"
              className="text-gold hover:text-[#E5C55D] text-xs uppercase tracking-[0.25em]"
              data-testid={`hub-list-cta-${category.slug}`}
            >
              List with us <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="subcategory-grid"
        >
          {category.subcategories.map((sub, idx) => {
            const SubIcon = getIcon(sub.icon);
            const href = sub.external ? sub.slug : categoryUrl(category.slug, sub.slug);
            return (
              <Link
                key={sub.slug}
                to={href}
                className="group relative overflow-hidden glass-light border border-white/5 hover:border-gold/40 transition-all duration-500 h-[340px]"
                data-testid={`subcategory-card-${sub.slug.replace(/\//g, '-')}`}
              >
                {sub.image && (
                  <>
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-110"
                      style={{ backgroundImage: `url('${sub.image}')` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/70 to-[#050C16]/10" />
                  </>
                )}
                <div className="relative h-full flex flex-col justify-between p-7">
                  <div className="flex items-center justify-between">
                    <span className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/80 group-hover:text-gold group-hover:border-gold/60 transition-colors">
                      <SubIcon size={16} />
                    </span>
                    {sub.badge && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-2 py-1">
                        {sub.badge}
                      </span>
                    )}
                    <span className="text-xs text-white/40 tabular-nums">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div>
                    <h3
                      className="text-2xl font-medium text-white mb-2 group-hover:text-gold transition-colors"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {sub.name}
                    </h3>
                    {sub.description && (
                      <p className="text-sm text-white/60 leading-relaxed line-clamp-2">
                        {sub.description}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold mt-4 group-hover:translate-x-1 transition-transform">
                      Explore <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16 flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Sell · List · Earn</p>
            <h3
              className="text-2xl sm:text-3xl font-medium text-white max-w-xl"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Reach Southeast Asia&apos;s most active yachting audience.
            </h3>
          </div>
          <div className="flex gap-3">
            <Link to="/list/new">
              <Button
                className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-6"
                data-testid={`hub-primary-cta-${category.slug}`}
              >
                Create a listing
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" className="text-white/80 hover:text-gold px-6 border border-white/15">
                Talk to us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
